import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ShoppingCart, ShieldCheck, ArrowRight, Sparkles, TrendingUp, Boxes } from "lucide-react";
import BdsLogo from "@/components/BdsLogo";
import usePageTitle from "@/hooks/usePageTitle";

export default function Landing() {
  usePageTitle("");
  const navigate = useNavigate();
  return (
    <div className="relative min-h-screen overflow-hidden bg-[#F8FAFC]">
      <div className="grain pointer-events-none absolute inset-0" />
      <div className="absolute -left-40 -top-40 h-96 w-96 rounded-full bg-indigo-200/40 blur-3xl" />
      <div className="absolute -bottom-40 right-0 h-96 w-96 rounded-full bg-emerald-200/40 blur-3xl" />

      <div className="relative mx-auto max-w-6xl px-6 py-12 lg:py-20">
        <div className="flex items-center gap-3">
          <BdsLogo size={44} showText={false} />
          <div className="flex items-center gap-2 text-indigo-700">
            <Sparkles className="h-5 w-5" />
            <span className="text-xs font-semibold uppercase tracking-[0.25em]">Retail Suite</span>
          </div>
        </div>

        <div className="mt-8 grid gap-12 lg:grid-cols-[1.1fr_0.9fr] lg:items-center">
          <div>
            <h1 className="font-outfit text-4xl font-bold leading-none text-slate-900 sm:text-5xl lg:text-6xl">
              Bharat Digital <span className="text-indigo-700">Seva</span>
            </h1>
            <p className="mt-5 max-w-xl text-base leading-relaxed text-slate-600">
              Stock, purchase, billing, customer CRM and profit analytics for your stationery store — built for
              lightning-fast counter billing and complete admin control.
            </p>

            <div className="mt-10 grid gap-4 sm:grid-cols-2">
              <motion.button
                whileHover={{ y: -4 }}
                data-testid="billing-counter-entry"
                onClick={() => navigate("/pos")}
                className="group bds-card bds-card-hover flex flex-col items-start gap-3 bg-gradient-to-br from-emerald-50 to-white p-6 text-left"
              >
                <span className="rounded-xl bg-emerald-600 p-3 text-white shadow-lg shadow-emerald-600/25">
                  <ShoppingCart className="h-6 w-6" />
                </span>
                <span className="font-outfit text-lg font-semibold text-slate-900">Billing Counter</span>
                <span className="text-sm text-slate-600">Direct POS access — no login needed.</span>
                <span className="mt-1 flex items-center gap-1 text-sm font-semibold text-emerald-700">
                  Start billing <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                </span>
              </motion.button>

              <motion.button
                whileHover={{ y: -4 }}
                data-testid="administrator-entry"
                onClick={() => navigate("/admin/login")}
                className="group bds-card bds-card-hover flex flex-col items-start gap-3 bg-gradient-to-br from-indigo-50 to-white p-6 text-left"
              >
                <span className="rounded-xl bg-indigo-600 p-3 text-white shadow-lg shadow-indigo-600/25">
                  <ShieldCheck className="h-6 w-6" />
                </span>
                <span className="font-outfit text-lg font-semibold text-slate-900">Administrator</span>
                <span className="text-sm text-slate-600">Stock, profit, reports & settings.</span>
                <span className="mt-1 flex items-center gap-1 text-sm font-semibold text-indigo-700">
                  Secure login <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                </span>
              </motion.button>
            </div>
          </div>

          <div className="grid gap-4">
            {[
              { icon: Boxes, title: "Live inventory control", text: "Every movement recorded in an immutable stock ledger." },
              { icon: TrendingUp, title: "Real profit visibility", text: "Weighted average costing with per-invoice profit." },
              { icon: Sparkles, title: "Notebook-only discounts", text: "5% above ₹500, 7% above ₹1,000 — automatic." },
            ].map((f, i) => (
              <motion.div
                key={f.title}
                initial={{ opacity: 0, x: 24 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.1 * i, duration: 0.4 }}
                className="bds-card bds-card-hover flex items-start gap-4 p-5"
              >
                <span className="rounded-xl bg-indigo-50 p-2.5 text-indigo-600">
                  <f.icon className="h-5 w-5" />
                </span>
                <div>
                  <p className="font-outfit font-semibold text-slate-900">{f.title}</p>
                  <p className="text-sm text-slate-600">{f.text}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        <p className="mt-16 text-xs text-slate-400">
          © {new Date().getFullYear()} Bharat Digital Seva · <Link className="underline" to="/pos">Counter</Link>
        </p>
      </div>
    </div>
  );
}
